import React, { useState, useMemo, Suspense } from "react";
import useThemeColors from "../../organization/portal/useThemeColors";
import useTranslation from "../../../i18n/useTranslation";
import { alphaFromHex } from "../../../util/colors";

import OrgPortalTeamsTab from "./OrgPortalTeamsTab";
import OrgPortalScheduleTab from "./OrgPortalScheduleTab";
import Standings from "../../season/Standings";
import StatsDashboard from "../../season/StatsDashboard";

export default function OrgPortalDivisionTabs({
                                                  teams,
                                                  games,
                                                  standings,
                                                  division,
                                                  activeSeasonData,
                                                  stages,
                                                  setTotalGames = 0,
                                              }) {
    const colors = useThemeColors();
    const { t } = useTranslation();

    const [activeTab, setActiveTab] = useState("teams");

    const ui = useMemo(() => ({
        shell: {
            borderRadius: 18,
            border: "1px solid rgba(0,0,0,0.08)",
            background: "#fff",
            boxShadow: "0 18px 44px rgba(0,0,0,0.10)",
            overflow: "hidden",
        },
        header: {
            padding: "12px 12px 10px",
            background: `linear-gradient(
                135deg,
                ${alphaFromHex(colors.primary, 0.45)},
                ${alphaFromHex(colors.primary, 0.0)}
            )`,
            borderBottom: "1px solid rgba(0,0,0,0.06)",
        },
        title: {
            margin: 0,
            fontSize: 13,
            fontWeight: 900,
            textTransform: "uppercase",
            color: colors.textOnPrimary,
        },
        tabs: {
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            marginTop: 10,
        },
        tab: {
            borderRadius: 999,
            padding: "8px 12px",
            fontSize: 13,
            fontWeight: 900,
            border: "1px solid rgba(0,0,0,0.10)",
            background: "#fff",
            cursor: "pointer",
            color: "rgba(20,21,26,0.75)",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            userSelect: "none",
            transition: "transform 120ms ease, box-shadow 120ms ease, background 120ms ease",
        },
        tabActive: {
            background: colors.primary,
            color: colors.textOnPrimary,
        },
        body: {
            padding: 14,
        },
    }), [colors]);

    const Tab = ({ id, label, icon }) => {
        const active = activeTab === id;

        return (
            <button
                onClick={() => setActiveTab(id)}
                style={{
                    ...ui.tab,
                    ...(active ? ui.tabActive : null),
                }}
            >
                {icon && <i className={`fas ${icon}`} />}
                {label}
            </button>
        );
    };

    return (
        <div style={ui.shell}>
            <div style={ui.header}>
                <h3 style={ui.title}>{division?.name}</h3>

                <div style={ui.tabs}>
                    <Tab id="teams" label={t("common.teams")} icon="fa-users" />
                    <Tab id="schedule" label={t("divisionTags.schedule")} icon="fa-list" />
                    <Tab id="standings" label={t("divisionTags.standings")} icon="fa-trophy" />
                    <Tab id="stats" label={t("divisionTags.stats")} icon="fa-trophy" />
                </div>
            </div>

            <div style={ui.body}>
                <Suspense fallback={<div>{t("divisionTags.loading")}</div>}>

                    {activeTab === "teams" && (
                        <OrgPortalTeamsTab teams={teams} />
                    )}

                    {activeTab === "schedule" && (
                        <OrgPortalScheduleTab games={games} activeSeasonData={activeSeasonData} />
                    )}

                    {activeTab === "standings" && (
                        <Standings
                            standingsByGroup={standings}
                            division={division}
                            season={activeSeasonData}
                        />
                    )}

                    {activeTab === "stats" && (
                        <div className="tab-pane fade show active" id="stats" role="tabpanel">
                            {["soccer", "hockey", "baseball", "softball"].includes(division?.sport?.key) ? (
                                <StatsDashboard
                                    sportKey={division.sport?.key}
                                    isActive={activeTab === "stats"}
                                    pro={true}
                                    seasonId={activeSeasonData?.id}
                                    stageId={activeSeasonData?.stage?.id || null}
                                />
                            ) : (
                                t("divisionTags.comingSoon")
                            )}
                        </div>
                    )}

                </Suspense>
            </div>
        </div>
    );
}
